import { useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Driver } from 'driver.js'
import { RootState } from 'store/modules/rootReducer'
import { markTimerFullScreenTourSeen } from 'store/modules/tour'
import { getTimerFullScreenTourConfig, createDriverInstance } from 'pages/Planning/utils/tourConfig'
import { trackTimerFullScreenTourShown } from 'pages/Planning/utils/tourTracking'
import { useTracking } from 'react-tracking'

interface UseTimerFullScreenTourProps {
  isDarkMode: boolean
}

export const useTimerFullScreenTour = ({ isDarkMode }: UseTimerFullScreenTourProps) => {
  const dispatch = useDispatch()
  const { trackEvent } = useTracking()
  const driverRef = useRef<Driver | null>(null)
  const tourState = useSelector((state: RootState) => state.tour.dashboard)

  // Check if element exists
  const elementExists = useCallback((selector: string): boolean => {
    try {
      const element = document.querySelector(selector)
      return element !== null && element instanceof HTMLElement
    } catch {
      return false
    }
  }, [])

  // Start timer fullscreen tour
  const startTour = useCallback(
    (force = false) => {
      // Don't show if already seen (unless forced)
      if (!force && tourState.timerFullScreenTourSeen) return

      // Check if timer fullscreen exists
      if (!elementExists('[data-tour="timer-fullscreen-minimize"]')) return

      // Get tour config
      const config = getTimerFullScreenTourConfig(isDarkMode)

      // Verify first element exists
      if (!elementExists(config[0].element)) return

      // Track tour shown (only if not forced)
      if (!force) {
        trackEvent(trackTimerFullScreenTourShown())
      }

      // Create driver instance with onDestroyed callback
      const handleTourComplete = () => {
        // Mark as seen when tour completes (only if not forced)
        if (!force) {
          dispatch(markTimerFullScreenTourSeen())
        }
      }

      const driverInstance = createDriverInstance(isDarkMode, handleTourComplete)

      // Configure driver with steps
      driverInstance.setSteps(
        config.map((stepConfig) => ({
          element: stepConfig.element,
          popover: {
            title: stepConfig.popover.title,
            description: stepConfig.popover.description,
            side: stepConfig.popover.side || 'top',
            align: stepConfig.popover.align || 'center',
          },
        }))
      )

      // Store the instance
      driverRef.current = driverInstance

      // Start tour with a small delay to ensure DOM is ready
      setTimeout(() => {
        if (driverRef.current) {
          try {
            driverRef.current.drive()
          } catch (error) {
            console.error('Error starting timer fullscreen tour:', error)
            // Mark as seen on error to prevent retry loops
            if (!force) {
              dispatch(markTimerFullScreenTourSeen())
            }
          }
        }
      }, 500)
    },
    [isDarkMode, tourState.timerFullScreenTourSeen, dispatch, elementExists, trackEvent]
  )

  // Auto-start tour when fullscreen appears and hasn't been seen
  useEffect(() => {
    if (
      !tourState.timerFullScreenTourSeen &&
      elementExists('[data-tour="timer-fullscreen-minimize"]')
    ) {
      const timer = setTimeout(() => {
        startTour()
      }, 1000) // Delay to ensure everything is loaded

      return () => clearTimeout(timer)
    }
  }, [tourState.timerFullScreenTourSeen, startTour, elementExists])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy()
      }
    }
  }, [])

  return {
    startTour,
  }
}
